import { useForm } from 'react-hook-form';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNotificationStore } from '../../../store/storeNotifications';

import { Box, Button, Paper, TextField, Tooltip } from '@mui/material';
import {
  type GridRenderCellParams,
  type GridColDef,
  type GridRowParams,
  GridActionsCellItem,
  DataGrid,
} from '@mui/x-data-grid';
import { Plus, Trash, Warning } from '@phosphor-icons/react';

import { metadataSchema } from './UploadItemMetadatas';

import type { VectorMetadataBulkTab } from './vectorModal';
import type { VectorMetadata } from '../../../types/vector';

interface VectorMetadataBulkChangeMetadataProps extends VectorMetadataBulkTab {
  /*
	onTempMetadataCreate: ({
		name, value
	}: {
		name: string, value: string
	}) => void
	*/
}

function VectorMetadataBulkChangeMetadata({
  filesIds,
  metadatasData,
  selectedTab,
  // onTempMetadataCreate,
}: VectorMetadataBulkChangeMetadataProps) {
  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    tab,

    selectedStagingFiles,
    selectedStoredFiles,

    onMetadataCreate,
    onMetadataDelete,
    onMetadataNameChange,
  } = useVectorModal();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm({
    defaultValues: {
      name: '',
      value: '',
    },
    resolver: zodResolver(metadataSchema),
  });

  const handleMetadataBulkCreate = ({ name, value }: VectorMetadata) => {
    const nameFormatted = name.toLowerCase().split(' ').join('_');

    const metadatas = metadatasData.map(value => value);

    const hasDuplicateKeyName = metadatas.find(
      metadata => metadata.name === nameFormatted,
    );

    if (hasDuplicateKeyName !== undefined) {
      setError('name', { message: 'Label already exists' });
      return;
    }

    // onTempMetadataCreate({ name, value })
    onMetadataCreate({ filesIds, metadatas: [{ name: nameFormatted, value }] });
    reset();
  };

  const handleMetadataBulkDelete = ({
    name,
    filesIds,
  }: {
    name: string;
    filesIds: Array<string>;
  }) => {
    onMetadataDelete({ filesIds, metadatas: [{ name }] });
  };

  const handleMetadataBulkNameChange = ({
    oldName,
    newName,
    filesIds,
  }: {
    oldName: string;
    newName: string;
    filesIds: Array<string>;
  }) => {
    onMetadataNameChange({ filesIds, metadatas: [{ newName, oldName }] });
  };

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Label Name',
      editable: true,
      width: 320,
      renderCell: (param: GridRenderCellParams<any, string>) => {
        const isMetadataPresentInEveryFile =
          tab === 'edit-stored-files'
            ? selectedStoredFiles.length === param.row.presentOn.length
            : selectedStagingFiles.length === param.row.presentOn.length;

        return (
          <Box
            component="span"
            sx={{
              width: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {param.value?.split('_').join(' ')}

            {!isMetadataPresentInEveryFile ? (
              <Tooltip
                title="This label does not exist in all documents"
                placement="top"
              >
                <Warning weight="fill" color="#ff9103" size={16} />
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'values',
      renderHeader: () => (
        <span
          style={{
            pointerEvents: 'none',
            opacity: '50%',
            userSelect: 'none',
            fontWeight: 500,
          }}
        >
          Label Value
        </span>
      ),
      width: 370,
      renderCell: (param: GridRenderCellParams<any, Array<string>>) => (
        <span
          style={{
            pointerEvents: 'none',
            opacity: '50%',
            userSelect: 'none',
          }}
        >
          {param.value?.join(', ')}
        </span>
      ),
      disableColumnMenu: true,
      disableReorder: true,
    },
    {
      field: 'delete',
      type: 'actions',
      width: 38,
      getActions: (params: GridRowParams) => [
        <Tooltip
          title="This will permanently delete this label from all the chosen files"
          placement="top"
        >
          <GridActionsCellItem
            sx={{
              padding: '0.5rem',
              borderRadius: 0,
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'transparent',

              transition: 'all 150ms ease-in',

              '&:hover': {
                backgroundColor: '#ffebee',
                color: 'red',
                borderColor: 'red',
              },

              '&>svg': {
                transition: 'all 150ms ease-in',
              },
            }}
            icon={<Trash size={16} />}
            label="Delete label"
            onClick={() =>
              handleMetadataBulkDelete({
                name: params.row.name,
                filesIds: params.row.presentOn,
              })
            }
          />
        </Tooltip>,
      ],
    },
  ];

  return (
    <Box
      role="tabpanel"
      hidden={selectedTab !== 'change-label'}
      id="tabpanel-bulk-metadata"
      aria-labelledby="tab-change-label"
      sx={{
        paddingInline: '2rem',
        '&>*+*': {
          marginBlockStart: '0.5rem !important',
        },
        '&[hidden]': {
          display: 'none',
        },
      }}
    >
      <Paper
        sx={{
          minWidth: 'auto !important',
          maxWidth: 'auto !important',

          boxShadow: 'none',
        }}
      >
        <DataGrid
          rows={metadatasData}
          columns={columns}
          getRowId={row => row.name}
          slotProps={{
            cell: {
              style: {
                textTransform: 'capitalize',
              },
            },
          }}
          processRowUpdate={(newRow, prevRow) => {
            const isMetadataDuplicate = metadatasData.find(
              metadata => metadata.name === newRow.name,
            );

            if (isMetadataDuplicate) {
              setNotification({
                message: 'Label already exists!',
                type: 'error',
              });

              return;
            }

            handleMetadataBulkNameChange({
              filesIds: prevRow.presentOn,
              newName: newRow.name,
              oldName: prevRow.name,
            });

            return newRow;
          }}
          onProcessRowUpdateError={error => {
            console.error(error);
          }}
        />
      </Paper>

      <Box
        id="bulk-add-new-label"
        component="form"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
        onSubmit={handleSubmit(handleMetadataBulkCreate)}
      >
        <TextField
          placeholder="Type Key here"
          sx={{ flex: 1 }}
          error={!!errors.name}
          helperText={errors.name?.message}
          {...register('name')}
        />

        <TextField
          placeholder="Type Value here"
          sx={{ flex: 1 }}
          error={!!errors.value}
          helperText={errors.value?.message}
          {...register('value')}
        />

        <Button type="submit" variant="outlined" form="bulk-add-new-label">
          Add
          <Plus size={16} />
        </Button>
      </Box>
    </Box>
  );
}

export { VectorMetadataBulkChangeMetadata };
